/**
 * Declarative description of editable landing-page content. The same schema
 * drives validation (resolveLandingContent), the first-paint shell rules,
 * and the Step 2 admin form.
 */
export interface TextField {
  type: "text";
  /** Dotted path. Relative to the item for list items; "" means the item itself. */
  path: string;
  label: string;
  max: number;
  multiline?: boolean;
  /** Optional fields may be omitted; invalid values are dropped. */
  optional?: boolean;
  pattern?: RegExp;
  /** Short hint shown under the field in the admin form. */
  help?: string;
}
export interface PhoneField {
  type: "phone";
  path: string;
  label: string;
  max: number;
}
export interface ListField {
  type: "list";
  path: string;
  label: string;
  itemLabel: string;
  /** min === maxItems means a fixed-length list, resolved item by item. */
  min: number;
  maxItems: number;
  /** An empty list is valid and means "use the built-in default". */
  allowEmpty?: boolean;
  /** Admin form: "showcase" picks websites from the portfolio. */
  editor?: "showcase";
  item: TextField[];
}
export interface VariantsField {
  type: "variants";
  path: string;
  label: string;
  maxItems: number;
  keyPattern: RegExp;
  requiredKey: string;
  item: TextField[];
}
export type LandingField = TextField | PhoneField | ListField | VariantsField;
export interface LandingFieldGroup {
  id: string;
  label: string;
  fields: LandingField[];
}

export interface LandingVariantText {
  eyebrow: string;
  highlight: string;
  rest: string;
}
export interface LandingShowcaseItem {
  id: string;
  name?: string;
  label?: string;
}
export interface LandingContent {
  phone: string;
  seo: { title: string; description: string };
  header: { callLabel: string };
  variants: Record<string, LandingVariantText>;
  hero: {
    intro: string;
    introMobile: string;
    checklist: string[];
    callPrompt: string;
  };
  booking: {
    title: string;
    subtitle: string;
    button: string;
    reassurance: string;
  };
  trust: { title: string; text: string }[];
  work: {
    eyebrow: string;
    title: string;
    subtitle: string;
    items: LandingShowcaseItem[];
  };
  process: {
    eyebrow: string;
    title: string;
    steps: { title: string; text: string }[];
  };
  cta: { label: string };
  questions: { eyebrow: string; title: string; items: { q: string; a: string }[] };
  finalCta: { title: string; text: string };
}

const text = (path: string, label: string, max: number, extra: Partial<TextField> = {}): TextField => ({
  type: "text",
  path,
  label,
  max,
  ...extra,
});

/** Shared schema for the "Direct Booking" landing layout. */
export const directBookingSchema: LandingFieldGroup[] = [
  {
    id: "listing",
    label: "Phone and search listing",
    fields: [
      { type: "phone", path: "phone", label: "Phone number", max: 20 },
      text("seo.title", "Search title", 70, {
        help: "Shown in the browser tab. This page is hidden from search engines.",
      }),
      text("seo.description", "Search description", 170, { multiline: true }),
      text("header.callLabel", "Header call label", 20, { help: "Small text before the number at the top of the page (tablet and desktop)." }),
    ],
  },
  {
    id: "variants",
    label: "Headline versions",
    fields: [
      {
        type: "variants",
        path: "variants",
        label: "Headline versions (picked with ?v= in the ad link)",
        maxItems: 12,
        keyPattern: /^[a-z0-9-]{1,30}$/,
        requiredKey: "default",
        item: [
          text("eyebrow", "Small line above the headline", 60),
          text("highlight", "Red headline words", 28),
          text("rest", "Rest of the headline", 48),
        ],
      },
    ],
  },
  {
    id: "hero",
    label: "Hero",
    fields: [
      text("hero.intro", "Intro (tablet and desktop)", 220, { multiline: true }),
      text("hero.introMobile", "Intro (phone)", 160, { multiline: true }),
      {
        type: "list",
        path: "hero.checklist",
        label: "Checklist",
        itemLabel: "Point",
        min: 3,
        maxItems: 3,
        item: [text("", "Point", 70)],
      },
      text("hero.callPrompt", "Call prompt", 30, { help: "Small text before the phone number under the checklist." }),
    ],
  },
  {
    id: "booking",
    label: "Booking card",
    fields: [
      text("booking.title", "Card title", 40),
      text("booking.subtitle", "Card subtitle", 90, { help: "Shown on tablet and desktop." }),
      text("booking.button", "Button", 20),
      text("booking.reassurance", "Reassurance line", 70, { help: "Shown under the button on tablet and desktop." }),
    ],
  },
  {
    id: "trust",
    label: "Trust points",
    fields: [
      {
        type: "list",
        path: "trust",
        label: "Trust points",
        itemLabel: "Point",
        min: 4,
        maxItems: 4,
        item: [text("title", "Title", 32), text("text", "Text", 60)],
      },
    ],
  },
  {
    id: "work",
    label: "Showcased websites",
    fields: [
      text("work.eyebrow", "Small heading", 24),
      text("work.title", "Title", 50),
      text("work.subtitle", "Subtitle", 110),
      {
        type: "list",
        path: "work.items",
        label: "Showcased websites (empty uses the default selection)",
        itemLabel: "Website",
        min: 3,
        maxItems: 6,
        allowEmpty: true,
        editor: "showcase",
        item: [
          text("id", "Portfolio item", 64, { pattern: /^[A-Za-z0-9-]{1,64}$/ }),
          text("name", "Name shown", 40, { optional: true }),
          text("label", "Label shown", 30, { optional: true }),
        ],
      },
    ],
  },
  {
    id: "process",
    label: "How it works",
    fields: [
      text("process.eyebrow", "Small heading", 24),
      text("process.title", "Title", 60),
      {
        type: "list",
        path: "process.steps",
        label: "Steps",
        itemLabel: "Step",
        min: 3,
        maxItems: 3,
        item: [text("title", "Title", 32), text("text", "Text", 170, { multiline: true })],
      },
    ],
  },
  {
    id: "questions",
    label: "Questions",
    fields: [
      text("questions.eyebrow", "Small heading", 24),
      text("questions.title", "Title", 60),
      {
        type: "list",
        path: "questions.items",
        label: "Questions",
        itemLabel: "Question",
        min: 1,
        maxItems: 10,
        item: [text("q", "Question", 110), text("a", "Answer", 420, { multiline: true })],
      },
    ],
  },
  {
    id: "closing",
    label: "Closing section",
    fields: [
      text("finalCta.title", "Title", 44),
      text("finalCta.text", "Text", 100),
      text("cta.label", "Book button (How it works, closing section, phone bar)", 24),
    ],
  },
];
